import isNullOrUndefined from "_shared/_utilities/is-null-or-undefined";
import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import AHDate from "_shared/dates/date";
import AssistantCitationSchema from "ai-assistant/citations/assistant-citation-schema";
import { Role } from "ai-assistant/roles/roles";
import { z } from "zod";

export type AssistantResponseJSON = z.infer<typeof AssistantResponseSchema.AssistantResponseJSONZ>;
export type AssistantResponseParams = z.infer<typeof AssistantResponseSchema.AssistantResponseZ>;

export default class AssistantResponseSchema {
  public static readonly AssistantResponseJSONZ = z.object({
    responseId: z.string().nullish(),
    role: z.enum(Role),
    text: z.string(),
    citations: z.array(AssistantCitationSchema.AssistantCitationJSONZ),
    timestamp: z.iso.datetime({ offset: true }),
  });

  public static readonly AssistantResponseZ = z.object({
    responseId: z.string().optional(),
    role: z.enum(Role),
    text: z.string(),
    citations: z.array(AssistantCitationSchema.AssistantCitationZ),
    timestamp: z.custom<AHDate>(),
  });

  public static validate(values: unknown): AssistantResponseParams {
    return parseOrThrow(
      AssistantResponseSchema.AssistantResponseZ, 
      values, 
      { 
        logError: true,
        schemaName: "AssistantResponseSchema",
        operation: "validate",
        payloadSample: values 
      }
    );
  }

  public static validateJSON(json: unknown): AssistantResponseJSON {
    return parseOrThrow(
      AssistantResponseSchema.AssistantResponseJSONZ, 
      json, 
      { 
        logError: true,
        schemaName: "AssistantResponseSchema",
        operation: "validateJSON",
        payloadSample: json 
      }
    );
  }

  public static isValid(values: unknown): values is AssistantResponseParams {
    try {
      AssistantResponseSchema.validate(values);
      return true;
    } catch {
      return false;
    }
  }

  public static isValidJSON(json: unknown): json is AssistantResponseJSON {
    try {
      AssistantResponseSchema.validateJSON(json);
      return true;
    } catch {
      return false;
    }
  }

  public static deserialize(json: AssistantResponseJSON): AssistantResponseParams {
    if (isNullOrUndefined(json)) return json;
    const parsed = parseOrThrow(
      AssistantResponseSchema.AssistantResponseJSONZ, 
      json, 
      { 
        logError: true,
        schemaName: "AssistantResponseSchema",
        operation: "deserialize",
        payloadSample: json 
      }
    );
    return AssistantResponseSchema.codec.decode(parsed);
  }

  public static serialize(legalEntity: AssistantResponseParams): AssistantResponseJSON {
    if (isNullOrUndefined(legalEntity)) return legalEntity;
    const parsed = AssistantResponseSchema.validate(legalEntity);
    if (isNullOrUndefined(parsed)) return parsed;
    return AssistantResponseSchema.codec.encode(parsed);
  }

  private static readonly codec = z.codec(
    AssistantResponseSchema.AssistantResponseJSONZ,
    AssistantResponseSchema.AssistantResponseZ,
    {
      decode: (data) => ({
        responseId: data.responseId ?? undefined,
        role: data.role as Role,
        text: data.text,
        citations: data.citations.map((citation) => AssistantCitationSchema.deserialize(citation)),
        timestamp: new AHDate(data.timestamp),
      }),
      encode: (response) => ({
        responseId: response.responseId,
        role: response.role,
        text: response.text,
        citations: response.citations.map((citation) => AssistantCitationSchema.serialize(citation)),
        timestamp: response.timestamp.toISOString(),
      }),
    }
  );
}
